// hooks/useAcknowledgements.ts
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts';
import { getAcknowledgements, setAcknowledgement } from '@/api';

function storageKey(email: string): string {
    return `infinitum_acks:${email}`;
}

function readCache(email: string): Record<string, string> {
    if (!email) return {};
    try {
        const raw = localStorage.getItem(storageKey(email));
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
}

export function useAcknowledgements() {
    const { authState } = useAuth();
    const email = authState.user?.email ?? '';

    const [acks, setAcks] = useState<Record<string, string>>(() => readCache(email));
    const [loaded, setLoaded] = useState(() => Object.keys(readCache(email)).length > 0);

    useEffect(() => {
        if (!authState.isAuth || !email) return;

        let cancelled = false;
        const key = storageKey(email);

        (async () => {
            try {
                const data = await getAcknowledgements();
                if (!cancelled) {
                    setAcks(data);
                    localStorage.setItem(key, JSON.stringify(data));
                }
            } catch {
                // API unavailable — use cached state (already initialised from localStorage)
            }
            if (!cancelled) setLoaded(true);
        })();

        return () => {
            cancelled = true;
        };
    }, [authState.isAuth, email]);

    const hasAcknowledged = useCallback(
        (key: string, value?: string): boolean => {
            const v = acks[key];
            if (v === undefined) return false;
            if (value !== undefined) return v === value;
            return true;
        },
        [acks]
    );

    const acknowledge = useCallback(
        async (key: string, value: string = 'true') => {
            setAcks((prev) => ({ ...prev, [key]: value }));

            try {
                const raw = localStorage.getItem(storageKey(email));
                const current = raw ? JSON.parse(raw) : {};
                current[key] = value;
                localStorage.setItem(storageKey(email), JSON.stringify(current));
            } catch {
                // Cache write failed — not critical
            }

            try {
                await setAcknowledgement(key, value);
            } catch {
                // API write failed — cached locally, will sync on next login
            }
        },
        [email]
    );

    return { loaded, hasAcknowledged, acknowledge };
}
